import { mmToMeshUnits } from '../brain/loadCortexMesh';
import type { NervousSystem } from '../pharmacology/types';

export type { NervousSystem };

/**
 * Anatomical landmarks for the nervous system beyond the brain.
 *
 * Everything here is expressed in **millimetres** in the same anatomical frame
 * the cortical mesh uses (+x = right, +y = superior, +z = anterior), with the
 * origin at the brain's centroid. Working in millimetres keeps the numbers
 * checkable against real anatomy instead of being scene units tuned by eye;
 * `toMesh()` converts them at the cortex mesh's own scale so the whole body
 * stays proportionate to the brain.
 *
 * Figures describe an approximately 1.75 m adult. They are illustrative
 * educational anatomy, not measurements from a specific atlas or subject.
 */

/** Converts a millimetre landmark into scene units. */
export function toMesh(x: number, y: number, z: number): [number, number, number] {
  return [mmToMeshUnits(x), mmToMeshUnits(y), mmToMeshUnits(z)];
}

/** A named point along the neuraxis, used to build the spinal cord path. */
export interface SpinalLevel {
  label: string;
  /** Superior-inferior position in mm (negative = below the brain centroid). */
  y: number;
  /** Anterior-posterior offset in mm — this is what gives the spine its curves. */
  z: number;
  /** Cord radius in mm at this level. */
  radius: number;
}

/**
 * The spinal cord from the foramen magnum to the conus medullaris, followed by
 * the cauda equina. The z offsets reproduce the real sagittal curvature —
 * cervical lordosis (anterior), thoracic kyphosis (posterior), lumbar lordosis
 * (anterior) — which is what stops the cord reading as a straight rod.
 *
 * The cord is widest at the cervical and lumbar enlargements, where the nerve
 * supply for the arms and legs originates.
 */
export const SPINAL_LEVELS: SpinalLevel[] = [
  { label: 'Medulla / foramen magnum', y: -55, z: -8, radius: 8 },
  { label: 'C1', y: -75, z: 0, radius: 8.5 },
  { label: 'C4 (cervical enlargement)', y: -120, z: 12, radius: 9.5 },
  { label: 'C7', y: -175, z: 8, radius: 9 },
  { label: 'T1', y: -200, z: 0, radius: 8 },
  { label: 'T4', y: -265, z: -14, radius: 7 },
  { label: 'T7', y: -330, z: -20, radius: 7 },
  { label: 'T10', y: -395, z: -12, radius: 8 },
  { label: 'T12 (lumbar enlargement)', y: -450, z: 4, radius: 9 },
  { label: 'L1 / conus medullaris', y: -495, z: 16, radius: 5 },
  { label: 'Cauda equina', y: -560, z: 20, radius: 3 },
  { label: 'S2 (dural sac)', y: -610, z: 14, radius: 2 },
];

export interface NervePath {
  id: string;
  label: string;
  system: NervousSystem;
  /** Short note shown when the pathway is highlighted. */
  description: string;
  /** Polyline in millimetres. Mirrored to the left side when `bilateral`. */
  points: [number, number, number][];
  bilateral: boolean;
  /** True anatomical trunk radius in millimetres, before display exaggeration. */
  radiusMm: number;
}

/**
 * Real nerve trunk diameters vary from roughly 15 mm (sciatic, the largest in
 * the body) down to about 1 mm for digital branches. Rendered at true scale the
 * finer nerves are sub-pixel when the whole body is in frame, so diameters are
 * exaggerated by a constant factor for legibility — the same trade already made
 * for the cerebellar folia. Relative thicknesses stay faithful, so the sciatic
 * still reads as far heavier than a digital nerve.
 */
export const NERVE_RADIUS_EXAGGERATION = 1.6;

/**
 * Peripheral, cranial and autonomic pathways.
 *
 * Simplified routes for the principal trunks and their major named branches —
 * still not a complete peripheral map, since the real peripheral nervous system
 * branches far beyond this. They are chosen to show where in the body each drug
 * class actually acts, which is the educational point.
 */
export const NERVE_PATHS: NervePath[] = [
  // ── Cranial nerves ──────────────────────────────────────────────────────
  {
    id: 'optic_nerve',
    label: 'Optic nerve (CN II)',
    system: 'somatic',
    description: 'Carries visual information from the retina back to the thalamus and visual cortex.',
    bilateral: true,
    radiusMm: 2.0,
    points: [
      [30, 8, 82],
      [22, 2, 55],
      [12, -6, 28],
      [16, -12, 2],
    ],
  },
  {
    id: 'trigeminal_nerve',
    label: 'Trigeminal nerve (CN V)',
    system: 'somatic',
    description: 'Sensation from the face and the nerve that dental anaesthetic blocks.',
    bilateral: true,
    radiusMm: 2.4,
    points: [
      [14, -42, -8],
      [34, -34, 26],
      [52, -26, 56],
      [60, -14, 76],
    ],
  },
  {
    id: 'facial_nerve',
    label: 'Facial nerve (CN VII)',
    system: 'somatic',
    description: 'Drives the muscles of facial expression; damage causes facial palsy.',
    bilateral: true,
    radiusMm: 1.6,
    points: [
      [15, -50, -14],
      [38, -46, 12],
      [58, -40, 40],
      [66, -30, 62],
    ],
  },
  {
    id: 'vagus',
    label: 'Vagus nerve (CN X)',
    system: 'autonomic',
    description:
      'Main parasympathetic outflow. Runs from the medulla down the neck to the heart, lungs and gut.',
    bilateral: true,
    radiusMm: 3.0,
    points: [
      [12, -50, 5],
      [22, -110, 25],
      [26, -175, 40],
      [24, -250, 45],
      [18, -320, 40],
      [12, -390, 30],
    ],
  },

  // ── Neck and thorax ─────────────────────────────────────────────────────
  {
    id: 'phrenic_nerve',
    label: 'Phrenic nerve',
    system: 'somatic',
    description:
      'From C3–C5 to the diaphragm — it drives breathing. A high spinal injury that cuts it stops respiration.',
    bilateral: true,
    radiusMm: 2.0,
    points: [
      [18, -108, 6],
      [28, -170, 24],
      [34, -240, 36],
      [38, -300, 40],
    ],
  },
  {
    id: 'sympathetic_chain',
    label: 'Sympathetic chain',
    system: 'autonomic',
    description:
      'Paired ganglia running alongside the vertebral column — the relay for sympathetic "fight or flight" outflow.',
    bilateral: true,
    radiusMm: 3.0,
    points: [
      [22, -95, -5],
      [24, -160, -8],
      [25, -230, -18],
      [25, -300, -22],
      [24, -370, -16],
      [22, -440, -4],
      [20, -500, 6],
    ],
  },
  {
    id: 'splanchnic_nerves',
    label: 'Splanchnic nerves',
    system: 'autonomic',
    description:
      'Carry sympathetic fibres from the chain to the abdominal organs via the coeliac ganglion.',
    bilateral: true,
    radiusMm: 2.2,
    points: [
      [24, -300, -20],
      [20, -320, 6],
      [14, -335, 30],
      [8, -345, 48],
    ],
  },

  // ── Upper limb ──────────────────────────────────────────────────────────
  {
    id: 'brachial_plexus',
    label: 'Brachial plexus',
    system: 'somatic',
    description: 'Nerve network formed from C5–T1 roots that supplies the shoulder, arm and hand.',
    bilateral: true,
    radiusMm: 8.0,
    points: [
      [18, -150, 0],
      [70, -170, 10],
      [125, -185, 14],
      [175, -195, 10],
    ],
  },
  {
    id: 'axillary_nerve',
    label: 'Axillary nerve',
    system: 'somatic',
    description: 'Wraps the shoulder to supply deltoid; vulnerable in shoulder dislocation.',
    bilateral: true,
    radiusMm: 2.6,
    points: [
      [160, -190, 8],
      [190, -205, -12],
      [200, -232, -6],
    ],
  },
  {
    id: 'musculocutaneous_nerve',
    label: 'Musculocutaneous nerve',
    system: 'somatic',
    description: 'Supplies the biceps and the flexors that bend the elbow.',
    bilateral: true,
    radiusMm: 2.4,
    points: [
      [175, -195, 12],
      [196, -270, 26],
      [210, -350, 30],
    ],
  },
  {
    id: 'radial_nerve',
    label: 'Radial nerve',
    system: 'somatic',
    description:
      'Spirals behind the humerus to extend the wrist and fingers. Compression causes wrist drop.',
    bilateral: true,
    radiusMm: 4.0,
    points: [
      [175, -196, 2],
      [198, -262, -18],
      [214, -350, -12],
      [224, -424, 8],
      [232, -520, 20],
      [238, -640, 22],
    ],
  },
  {
    id: 'median_nerve',
    label: 'Median nerve',
    system: 'somatic',
    description:
      'Runs down the arm to the hand; compressed at the wrist it produces carpal tunnel syndrome.',
    bilateral: true,
    radiusMm: 5.0,
    points: [
      [175, -195, 10],
      [205, -300, 18],
      [222, -420, 22],
      [232, -540, 20],
      [236, -650, 16],
    ],
  },
  {
    id: 'ulnar_nerve',
    label: 'Ulnar nerve',
    system: 'somatic',
    description:
      'Passes behind the elbow — the "funny bone" nerve — and supplies most small muscles of the hand.',
    bilateral: true,
    radiusMm: 4.0,
    points: [
      [175, -195, 4],
      [200, -300, -4],
      [216, -420, -10],
      [228, -540, -6],
      [234, -650, 0],
    ],
  },
  {
    id: 'digital_nerves',
    label: 'Digital nerves',
    system: 'somatic',
    description:
      'The fine terminal branches supplying the fingers — among the densest sensory territory in the body.',
    bilateral: true,
    radiusMm: 1.2,
    points: [
      [236, -650, 14],
      [244, -700, 20],
      [250, -745, 22],
      [252, -772, 20],
    ],
  },

  // ── Lower limb ──────────────────────────────────────────────────────────
  {
    id: 'lumbosacral_plexus',
    label: 'Lumbosacral plexus',
    system: 'somatic',
    description: 'Formed from L1–S4 roots; gives rise to the femoral, obturator and sciatic nerves.',
    bilateral: true,
    radiusMm: 8.0,
    points: [
      [16, -470, 8],
      [45, -530, 4],
      [72, -590, -4],
      [88, -640, -10],
    ],
  },
  {
    id: 'femoral_nerve',
    label: 'Femoral nerve',
    system: 'somatic',
    description: 'Runs down the front of the thigh; drives knee extension and front-of-leg sensation.',
    bilateral: true,
    radiusMm: 6.0,
    points: [
      [72, -590, 12],
      [86, -720, 22],
      [92, -870, 24],
      [94, -1000, 18],
    ],
  },
  {
    id: 'saphenous_nerve',
    label: 'Saphenous nerve',
    system: 'somatic',
    description: 'The femoral nerve’s long sensory branch, reaching the inner ankle.',
    bilateral: true,
    radiusMm: 2.0,
    points: [
      [94, -1000, 18],
      [88, -1120, 14],
      [82, -1280, 10],
      [80, -1390, 8],
    ],
  },
  {
    id: 'obturator_nerve',
    label: 'Obturator nerve',
    system: 'somatic',
    description: 'Supplies the adductor muscles that pull the thigh inward.',
    bilateral: true,
    radiusMm: 3.0,
    points: [
      [60, -600, 2],
      [66, -700, 14],
      [70, -820, 18],
    ],
  },
  {
    id: 'sciatic_nerve',
    label: 'Sciatic nerve',
    system: 'somatic',
    description:
      'The largest nerve in the body, about as thick as a finger. Runs from the pelvis down the back of the thigh.',
    bilateral: true,
    radiusMm: 15.0,
    points: [
      [88, -640, -10],
      [96, -780, -14],
      [100, -900, -12],
      [100, -1000, -8],
    ],
  },
  {
    id: 'tibial_nerve',
    label: 'Tibial nerve',
    system: 'somatic',
    description:
      'One of the two nerves the sciatic splits into behind the knee; runs down the calf to the sole.',
    bilateral: true,
    radiusMm: 8.0,
    points: [
      [100, -1000, -8],
      [100, -1120, -12],
      [98, -1260, -8],
      [96, -1380, 0],
    ],
  },
  {
    id: 'common_peroneal_nerve',
    label: 'Common peroneal nerve',
    system: 'somatic',
    description:
      'The sciatic’s other branch, winding around the outside of the knee — where it is easily injured, causing foot drop.',
    bilateral: true,
    radiusMm: 5.0,
    points: [
      [100, -1000, -8],
      [112, -1040, 2],
      [110, -1160, 14],
      [104, -1300, 18],
      [100, -1390, 16],
    ],
  },
  {
    id: 'plantar_nerves',
    label: 'Plantar nerves',
    system: 'somatic',
    description: 'Terminal branches supplying the sole of the foot.',
    bilateral: true,
    radiusMm: 1.6,
    points: [
      [96, -1380, 0],
      [94, -1420, 16],
      [92, -1432, 42],
    ],
  },

  // ── Enteric ─────────────────────────────────────────────────────────────
  {
    id: 'enteric_plexus',
    label: 'Enteric nervous system',
    system: 'enteric',
    description:
      'The gut’s own neural network — often called the "second brain". Roughly 500 million neurons acting largely independently of the CNS.',
    bilateral: false,
    radiusMm: 4.0,
    points: [
      [-62, -318, 58],
      [4, -292, 72],
      [68, -330, 64],
      [78, -392, 66],
      [26, -424, 74],
      [-38, -436, 70],
      [-76, -382, 60],
      [-52, -324, 56],
    ],
  },
];

/**
 * Intercostal nerves — the segmental thoracic nerves that wrap around the rib
 * cage from the spine toward the sternum. Generated rather than hand-listed
 * because they are a regular repeating series; hand-writing twelve near-identical
 * entries would add noise without adding information.
 */
export function intercostalNerves(): NervePath[] {
  const paths: NervePath[] = [];
  const count = 9;

  for (let i = 0; i < count; i++) {
    const t = i / (count - 1);
    const y = -215 - t * 190;
    // Lower ribs sit further forward and angle down more steeply as they descend.
    const reach = 78 + 34 * Math.sin(Math.PI * t);
    const drop = 26 + 34 * t;

    paths.push({
      id: `intercostal_${i + 1}`,
      label: `Intercostal nerve T${i + 1}`,
      system: 'somatic',
      description: 'Segmental nerve running between the ribs, supplying chest wall and skin.',
      bilateral: true,
      radiusMm: 1.8,
      points: [
        [20, y, spinalCurveZ(y) - 6],
        [reach * 0.55, y - drop * 0.35, 8],
        [reach * 0.85, y - drop * 0.75, 38],
        [reach * 0.6, y - drop, 62],
      ],
    });
  }

  return paths;
}

/** Every pathway, including the generated intercostal series. */
export function allNervePaths(): NervePath[] {
  return [...NERVE_PATHS, ...intercostalNerves()];
}

/** Spinal nerve root pairs, drawn as short segments branching from the cord. */
export interface SpinalNerveRoot {
  y: number;
  /** Lateral reach in mm. */
  reach: number;
  z: number;
}

/**
 * 31 pairs of spinal nerves, spaced down the cord. Reach is longer at the
 * cervical and lumbar enlargements where the limb plexuses form.
 */
export function spinalNerveRoots(): SpinalNerveRoot[] {
  const roots: SpinalNerveRoot[] = [];
  const top = -70;
  const bottom = -560;
  const count = 31;

  for (let i = 0; i < count; i++) {
    const t = i / (count - 1);
    const y = top + (bottom - top) * t;
    // Cervical (t<0.25) and lumbar (t>0.72) enlargements carry the limb supply.
    const cervical = Math.exp(-(((t - 0.12) / 0.1) ** 2));
    const lumbar = Math.exp(-(((t - 0.82) / 0.12) ** 2));
    const reach = 26 + 26 * Math.max(cervical, lumbar);
    // Follow the cord's own curvature so roots leave from the right place.
    const z = spinalCurveZ(y);
    roots.push({ y, reach, z });
  }

  return roots;
}

/** Interpolates the cord's anterior-posterior offset at a given height, in mm. */
export function spinalCurveZ(y: number): number {
  const levels = SPINAL_LEVELS;
  if (y >= levels[0].y) return levels[0].z;
  if (y <= levels[levels.length - 1].y) return levels[levels.length - 1].z;

  for (let i = 0; i < levels.length - 1; i++) {
    const a = levels[i];
    const b = levels[i + 1];
    if (y <= a.y && y >= b.y) {
      const t = (a.y - y) / (a.y - b.y);
      return a.z + (b.z - a.z) * t;
    }
  }
  return 0;
}

/** Vertical extent of the whole nervous system in mm, used for camera framing. */
export const BODY_EXTENT_MM = {
  top: 90,
  bottom: -1430,
};
